import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import DuplicateBadge from '../components/DuplicateBadge';
import { formatSize } from '../utils/variantUtils';
import {
    getShippingQueue,
    sendToDelivery,
    bulkSendToDelivery,
    getDeliveryCompanies,
    confirmOrder
} from '../services/api';
import {
    Truck,
    Package,
    Phone,
    MapPin,
    Clock,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Search,
    SearchX,
    User,
    AlertTriangle,
    ExternalLink,
    Send
} from 'lucide-react';

const ShippingQueue = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [actionLoading, setActionLoading] = useState({});
    const [isBulkLoading, setIsBulkLoading] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [ordersRes, providersRes] = await Promise.all([
                getShippingQueue(),
                getDeliveryCompanies()
            ]);
            setOrders(ordersRes);
            const connectedProviders = providersRes.data.filter(p => p.isConnected);
            setProviders(connectedProviders);
        } catch (error) {
            showToast('error', t('shipping.messages.fetch_error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const showToast = (type, message) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    const handleSendToDelivery = async (orderId, providerSlug) => {
        if (!providerSlug) {
            showToast('error', t('shipping.messages.no_provider'));
            return;
        }

        const provider = providers.find(p => p.slug === providerSlug);
        if (!provider) {
            showToast('error', t('shipping.messages.provider_not_connected', { provider: providerSlug }));
            return;
        }

        setActionLoading(prev => ({ ...prev, [orderId]: true }));
        try {
            const res = await sendToDelivery(orderId, providerSlug);
            if (res.success && res.tracking) {
                showToast('success', t('shipping.messages.send_success', { message: res.message, tracking: res.tracking }));
                setOrders(prev => prev.filter(o => o._id !== orderId));
            } else {
                showToast('error', t('shipping.messages.ozon_success_no_id'));
            }
        } catch (error) {
            const data = error.response?.data || {};
            const msg = data.message || t('shipping.messages.send_error');
            showToast('error', msg);
        } finally {
            setActionLoading(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const handleOpenDetails = (order) => {
        setSelectedOrder(order);
        setIsModalOpen(true);
    };

    const handleSaveConfirmation = async (orderId, confirmationData) => {
        try {
            await confirmOrder(orderId, confirmationData);
            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            throw err;
        }
    };

    const filteredOrders = orders.filter(order => {
        const query = searchQuery.toLowerCase();
        const ship = order.shipping || {};
        return (
            (ship.receiverName || order.name || "").toLowerCase().includes(query) ||
            (ship.phone || order.phone || "").toLowerCase().includes(query) ||
            (ship.cityName || order.city || "").toLowerCase().includes(query) ||
            order.orderId.toLowerCase().includes(query)
        );
    });

    const handleSelectOrder = (orderId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = new Set(filteredOrders.map(o => o._id));
            setSelectedIds(allIds);
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleBulkSend = async () => {
        if (selectedIds.size === 0) return;

        // Use the provider from the first selected order (assuming they are same for now or handle mixed)
        // Usually shipping queue is filtered or orders have pre-set providers
        const firstOrder = orders.find(o => o._id === Array.from(selectedIds)[0]);
        const providerSlug = firstOrder?.shipping?.providerSlug || 'ozonexpress';

        if (!window.confirm(t('shipping.messages.confirm_bulk_send', { count: selectedIds.size }))) return;

        setIsBulkLoading(true);
        try {
            const res = await bulkSendToDelivery(Array.from(selectedIds), providerSlug);
            if (res.success) {
                showToast('success', t('shipping.messages.bulk_send_success', {
                    succeeded: res.succeeded,
                    failed: res.failed
                }));
                // Remove succeeded orders from local state
                const succeededIds = res.results.success.map(s => s.id);
                setOrders(prev => prev.filter(o => !succeededIds.includes(o._id)));
                setSelectedIds(new Set());
            }
        } catch (error) {
            showToast('error', t('shipping.messages.bulk_send_error'));
        } finally {
            setIsBulkLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                <p className="ml-3 text-gray-400 font-bold uppercase tracking-widest text-xs">{t('dashboard.messages.loading')}</p>
            </div>
        );
    }

    return (
        <div className="px-4 py-6 md:p-8 max-w-7xl mx-auto pb-24 space-y-6 md:space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                        <Truck className="w-6 h-6 md:w-8 md:h-8 text-indigo-600" />
                        {t('shipping.title')}
                    </h1>
                    <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] md:text-[10px] mt-1 ml-0.5">
                        {t('shipping.ready_orders', { count: orders.length })}
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                    <div className="relative w-full md:w-72 group">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                        <input
                            type="text"
                            placeholder={t('shipping.search_placeholder')}
                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all text-xs md:text-sm font-bold text-gray-900"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    {filteredOrders.length > 0 && (
                        <label className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm cursor-pointer hover:border-indigo-500 transition-all group shrink-0 w-full sm:w-auto justify-center">
                            <input
                                type="checkbox"
                                className="sr-only"
                                onChange={handleSelectAll}
                                checked={selectedIds.size === filteredOrders.length && filteredOrders.length > 0}
                            />
                            <div className={`w-4 h-4 rounded border-2 transition-all flex items-center justify-center ${selectedIds.size === filteredOrders.length && filteredOrders.length > 0
                                ? 'bg-indigo-600 border-indigo-600 shadow-sm shadow-indigo-100'
                                : 'border-gray-200 bg-white group-hover:border-indigo-300'
                                }`}>
                                {selectedIds.size === filteredOrders.length && filteredOrders.length > 0 && (
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                            <span className="text-[10px] md:text-[11px] font-black uppercase tracking-widest text-gray-400 group-hover:text-indigo-600 transition-colors">
                                {t('shipping.select_all')}
                            </span>
                        </label>
                    )}
                </div>
            </div>

            {/* List */}
            <div className="space-y-4">
                {filteredOrders.length === 0 ? (
                    <div className="bg-white rounded-3xl p-16 text-center border border-gray-100 shadow-sm">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <SearchX className="w-8 h-8 text-gray-300" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{t('shipping.empty.title')}</h3>
                        <p className="text-gray-500 text-sm font-medium max-w-sm mx-auto">{t('shipping.empty.subtitle')}</p>
                    </div>
                ) : (
                    filteredOrders.map((order) => {
                        const ship = order.shipping || {};
                        const name = ship.receiverName || order.name;
                        const phone = ship.phone || order.phone;
                        const city = ship.cityName || order.city;
                        const address = ship.address || order.address;
                        const amount = ship.price || order.price || 0;
                        const confirmedAt = order.confirmationData?.confirmedAt
                            ? new Date(order.confirmationData.confirmedAt).toLocaleString(i18n.language, {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })
                            : t('shipping.order_card.confirmed_recently');

                        return (
                            <div
                                key={order._id}
                                className={`rounded-xl md:rounded-2xl border transition-all duration-300 overflow-hidden group ${order.isPotentialDuplicate
                                    ? 'bg-amber-50/30 border-amber-200 shadow-sm'
                                    : 'bg-white border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-100'
                                    }`}
                            >
                                <div className="p-4 md:p-5 flex flex-col lg:flex-row gap-4 md:gap-5 items-start lg:items-center relative">
                                    {/* Checkbox */}
                                    <label className="relative cursor-pointer group/check shrink-0 mt-1 md:mt-0">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(order._id)}
                                            onChange={() => handleSelectOrder(order._id)}
                                            className="sr-only"
                                        />
                                        <div className={`w-5 h-5 rounded md:rounded-md border-2 transition-all flex items-center justify-center ${selectedIds.has(order._id)
                                            ? 'bg-indigo-600 border-indigo-600 shadow-sm shadow-indigo-200'
                                            : 'border-gray-200 bg-white group-hover/check:border-indigo-300'
                                            }`}>
                                            {selectedIds.has(order._id) && (
                                                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                    </label>

                                    {/* Product Image */}
                                    <div className="hidden md:flex w-16 h-16 bg-indigo-50 rounded-2xl items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-500 overflow-hidden border border-gray-100/50 shrink-0">
                                        {order.productImage ? (
                                            <img
                                                src={order.productImage}
                                                alt={order.productName}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.parentElement.innerHTML = '<Package size={28} />'; }}
                                            />
                                        ) : (
                                            <Package size={28} />
                                        )}
                                    </div>

                                    {/* Order Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-1.5 md:mb-1">
                                            <span className="text-base md:text-lg font-black text-gray-900 tracking-tight">#{order.orderId}</span>
                                            <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-600 rounded-lg text-[9px] md:text-[10px] font-black uppercase tracking-widest border border-green-100">
                                                <CheckCircle2 size={10} /> {t('status.order.confirmed')}
                                            </div>
                                            <DuplicateBadge order={order} />
                                        </div>

                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-xs md:text-sm font-black text-indigo-600 truncate">
                                                {order.productName || "Produit"} — 
                                                <span className="ml-1 text-gray-600">
                                                    {typeof ship.variant === 'object' 
                                                        ? [ship.variant.color, formatSize(ship.variant.size)].filter(Boolean).join(" - ") 
                                                        : (formatSize(ship.variant) || t('orders.table.standard_variant'))}
                                                </span>
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 text-[9px] md:text-[10px] font-bold text-gray-400 mb-4 uppercase tracking-widest">
                                            <Clock size={10} className="md:w-[12px] md:h-[12px]" />
                                            {t('shipping.order_card.confirmed_by', {
                                                time: confirmedAt,
                                                name: order.confirmationData?.confirmedBy?.name || 'Admin'
                                            })}
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 md:gap-y-3 gap-x-8">
                                            <div className="flex items-center gap-2.5 md:gap-3">
                                                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 shrink-0">
                                                    <User size={12} className="md:w-[14px] md:h-[14px]" />
                                                </div>
                                                <span className="font-bold text-gray-900 text-[13px] md:text-sm truncate">{name}</span>
                                            </div>
                                            <div className="flex items-center gap-2.5 md:gap-3">
                                                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 shrink-0">
                                                    <Phone size={12} className="md:w-[14px] md:h-[14px]" />
                                                </div>
                                                <span className="font-bold text-gray-900 text-[13px] md:text-sm tracking-tight">{phone}</span>
                                            </div>
                                            <div className="flex items-center gap-2.5 md:gap-3 col-span-1 sm:col-span-2">
                                                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 shrink-0">
                                                    <MapPin size={12} className="md:w-[14px] md:h-[14px]" />
                                                </div>
                                                <span className="font-bold text-gray-900 text-[13px] md:text-sm line-clamp-1">{city} — {address}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Amount & Actions */}
                                    <div className="w-full lg:w-72 flex flex-col gap-2.5 lg:pl-5 lg:border-l lg:border-gray-50 mt-2 lg:mt-0">
                                        <div className="flex justify-between items-center px-4 py-2.5 bg-gray-50/50 rounded-xl border border-gray-100">
                                            <span className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('shipping.order_card.provider')}</span>
                                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] md:text-[10px] font-black uppercase tracking-widest border border-indigo-100 italic">
                                                {ship.providerSlug || t('shipping.order_card.not_selected')}
                                            </span>
                                        </div>

                                        <div className="flex justify-between items-center px-4 py-2.5 bg-gray-50/50 rounded-xl border border-gray-100">
                                            <span className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('shipping.order_card.amount_to_collect')}</span>
                                            <span className="text-base md:text-lg font-black text-indigo-600">
                                                {amount.toLocaleString()} <span className="text-[10px]">{t('shipping.order_card.amount_currency')}</span>
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2 mt-1">
                                            <button
                                                onClick={() => handleOpenDetails(order)}
                                                className="w-full flex items-center justify-center gap-2 py-3 md:py-2.5 bg-white border border-gray-200 hover:border-indigo-500 hover:text-indigo-600 text-gray-600 rounded-xl md:rounded-2xl font-black transition-all uppercase tracking-widest text-[9px] md:text-[10px]"
                                            >
                                                <ExternalLink size={12} className="md:w-[14px] md:h-[14px]" />
                                                <span>{t('shipping.order_card.details_button')}</span>
                                            </button>

                                            <button
                                                onClick={() => handleSendToDelivery(order._id, ship.providerSlug)}
                                                disabled={actionLoading[order._id] || !ship.providerSlug}
                                                className="w-full flex items-center justify-center gap-2 py-3 md:py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl md:rounded-2xl font-black transition-all shadow-sm uppercase tracking-widest text-[9px] md:text-[10px] disabled:opacity-50"
                                            >
                                                {actionLoading[order._id] ? (
                                                    <Loader2 className="animate-spin" size={14} />
                                                ) : (
                                                    <>
                                                        <Send size={12} className="md:w-[14px] md:h-[14px]" />
                                                        <span>{t('shipping.order_card.send_button')}</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Toasts */}
            <div className="fixed top-6 right-6 z-[110] flex flex-col gap-3">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`flex items-center gap-3 px-6 py-4 rounded-3xl shadow-2xl border animate-in slide-in-from-right-full duration-500 ${toast.type === 'success'
                            ? 'bg-white border-green-100 text-gray-900'
                            : 'bg-white border-red-100 text-gray-900'
                            }`}
                    >
                        {toast.type === 'success' ? (
                            <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : (
                            <AlertCircle className="w-6 h-6 text-red-500" />
                        )}
                        <span className="font-bold text-sm tracking-tight">{toast.message}</span>
                    </div>
                ))}
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] w-full max-w-xl px-4 animate-in slide-in-from-bottom-full duration-500">
                    <div className="bg-gray-900 text-white p-2.5 md:p-3 rounded-2xl shadow-2xl border border-white/10 flex items-center justify-between gap-4 backdrop-blur-xl bg-opacity-95">
                        <div className="flex items-center gap-3 ml-1">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-600 rounded-full flex items-center justify-center font-black text-xs md:text-sm shadow-lg shadow-indigo-500/20">
                                {selectedIds.size}
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-indigo-400 leading-none mb-1">Selection</span>
                                <span className="text-[11px] md:text-xs font-bold tracking-tight leading-none text-gray-100">
                                    {selectedIds.size === 1 ? t('shipping.bulk_action_title_single') : t('shipping.bulk_action_title_multiple', { count: selectedIds.size })}
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={handleBulkSend}
                            disabled={isBulkLoading}
                            className="bg-white text-gray-900 px-4 py-2.5 md:px-6 md:py-3 rounded-xl md:rounded-2xl font-black text-[10px] md:text-[11px] uppercase tracking-widest hover:bg-indigo-50 transition-all flex items-center gap-2 disabled:opacity-50 shrink-0 shadow-lg shadow-white/5"
                        >
                            {isBulkLoading ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <>
                                    <Send size={14} />
                                    <span className="hidden sm:inline">{t('shipping.send_selected_button')}</span>
                                    <span className="sm:hidden">Send</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Modal */}
            {selectedOrder && (
                <ConfirmationModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    order={selectedOrder}
                    onSave={handleSaveConfirmation}
                    readOnly={false}
                />
            )}
        </div>
    );
};

export default ShippingQueue;
